import React, { useState, useEffect } from 'react';
import {
  Box, Typography, CircularProgress, IconButton, Tooltip,
  Chip,
} from '@mui/material';
import LinkIcon       from '@mui/icons-material/Link';
import QrCodeIcon     from '@mui/icons-material/QrCode';
import EmailIcon      from '@mui/icons-material/Email';
import CheckIcon      from '@mui/icons-material/Check';
import BarChartIcon   from '@mui/icons-material/BarChart';
import BlockIcon      from '@mui/icons-material/Block';
import { listApplabShares, deactivateShare } from '../../pitch/services/pitchService';
import { APPLAB_ACCENT } from '../constants';
import QRDialog          from '../../shared/sharing/QRDialog';
import AppLabMailDialog  from './AppLabMailDialog';
import ShareStatsDialog  from './ShareStatsDialog';

export default function SharesTab({ app }) {
  const [shares,  setShares]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [qrShare,   setQrShare]   = useState(null);
  const [mailShare, setMailShare] = useState(null);
  const [statsShare, setStatsShare] = useState(null);

  useEffect(() => {
    if (!app?.id) return;
    setLoading(true);
    listApplabShares(app.id)
      .then(setShares)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [app?.id]);

  async function handleDeactivate(share) {
    await deactivateShare(share.id);
    setShares(s => s.map(x => x.id === share.id ? { ...x, shareEnabled: false } : x));
  }

  function copyLink(share) {
    const url = `${window.location.origin}/p/${share.shareToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(share.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
        <CircularProgress size={24} sx={{ color: APPLAB_ACCENT }} />
      </Box>
    );
  }

  if (shares.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center', color: '#555', fontSize: '0.85rem' }}>
        Inga delningar ännu. Tryck "Dela" för att skapa en publik länk.
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
      {shares.map(share => {
        const url       = `${window.location.origin}/p/${share.shareToken}`;
        const created   = share.createdAt?.toDate?.()?.toLocaleDateString('sv-SE') ?? '–';
        const expires   = share.expires_at?.toDate?.()?.toLocaleDateString('sv-SE') ?? null;
        const isExpired = share.expires_at?.toDate?.() && share.expires_at.toDate() < new Date();
        const active    = share.shareEnabled && !isExpired;

        return (
          <Box key={share.id} sx={{
            bgcolor: 'rgba(255,255,255,0.03)',
            border: `1px solid ${active ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.04)'}`,
            borderRadius: 1.5, px: 2, py: 1.5,
            opacity: active ? 1 : 0.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography fontWeight={700} sx={{ fontSize: '0.85rem', color: active ? 'white' : '#777', flex: 1, minWidth: 0 }} noWrap>
                {share.title || 'Delning'}
              </Typography>
              {active ? (
                <Chip label="Aktiv" size="small" sx={{ bgcolor: 'rgba(0,137,123,0.2)', color: APPLAB_ACCENT, fontSize: '0.65rem', height: 18 }} />
              ) : (
                <Chip label={isExpired ? 'Utgången' : 'Inaktiverad'} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: '#666', fontSize: '0.65rem', height: 18 }} />
              )}
            </Box>

            <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 1, fontFamily: 'monospace', fontSize: '0.7rem' }} noWrap>
              {url}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" sx={{ color: '#555', mr: 1 }}>
                Skapad {created}{expires ? ` · Utgår ${expires}` : ''}
              </Typography>

              <Tooltip title={copiedId === share.id ? 'Kopierat!' : 'Kopiera länk'}>
                <IconButton size="small" onClick={() => copyLink(share)} sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT } }}>
                  {copiedId === share.id ? <CheckIcon sx={{ fontSize: 15 }} /> : <LinkIcon sx={{ fontSize: 15 }} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="QR-kod">
                <IconButton size="small" onClick={() => setQrShare(share)} sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT } }}>
                  <QrCodeIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Skicka mejl">
                <IconButton size="small" onClick={() => setMailShare(share)} sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT } }}>
                  <EmailIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Statistik">
                <IconButton size="small" onClick={() => setStatsShare(share)} sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT } }}>
                  <BarChartIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              {active && (
                <Tooltip title="Avaktivera delning">
                  <IconButton size="small" onClick={() => handleDeactivate(share)}
                    sx={{ color: '#555', '&:hover': { color: '#ef5350' }, ml: 'auto' }}>
                    <BlockIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        );
      })}

      {qrShare && (
        <QRDialog
          open={!!qrShare}
          onClose={() => setQrShare(null)}
          url={`${window.location.origin}/p/${qrShare.shareToken}`}
          title={qrShare.title || app?.name}
        />
      )}
      {mailShare && (
        <AppLabMailDialog
          open={!!mailShare}
          onClose={() => setMailShare(null)}
          shareUrl={`${window.location.origin}/p/${mailShare.shareToken}`}
          appName={mailShare.title || app?.name || ''}
          description={mailShare.tagline || ''}
        />
      )}
      {statsShare && (
        <ShareStatsDialog
          open={!!statsShare}
          onClose={() => setStatsShare(null)}
          share={statsShare}
        />
      )}
    </Box>
  );
}
